
import { useState, useEffect, RefObject, useCallback } from 'react';
import { AsteroidsGameEngine } from '../AsteroidsGameEngine';
import { debounce } from '../utils';
import { useToast } from '@/components/ui/use-toast';
import { recordAnalyticsEvent } from '@/services/analyticsService';

interface UseAsteroidsEngineProps {
  canvasRef: RefObject<HTMLCanvasElement>;
  containerRef: RefObject<HTMLDivElement>;
}

export const useAsteroidsEngine = ({ canvasRef, containerRef }: UseAsteroidsEngineProps) => {
  const [gameEngine, setGameEngine] = useState<AsteroidsGameEngine | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Use debounced resize handler to prevent excessive calculations during resize
  const updateCanvasSize = useCallback(debounce(() => {
    if (!canvasRef.current || !containerRef.current) return;
    
    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // Keep aspect ratio but fit within container
    const aspectRatio = 4/3;
    let width = containerWidth;
    let height = width / aspectRatio;
    
    // If height is too large, constrain by height instead
    if (height > containerHeight) {
      height = containerHeight;
      width = height * aspectRatio;
    }
    
    // Set canvas dimensions
    canvas.width = Math.max(300, Math.min(800, width));
    canvas.height = Math.max(225, Math.min(600, height));
    
    // Update engine if it exists
    if (gameEngine) {
      gameEngine.resizeCanvas(canvas.width, canvas.height);
    }
    
    console.log('Canvas size updated to:', canvas.width, 'x', canvas.height);
  }, 150), [canvasRef, containerRef, gameEngine]);
  
  const initializeEngine = useCallback(() => {
    try {
      if (!canvasRef.current) {
        console.error('Canvas reference is not available');
        setError('Canvas reference is not available. Please refresh the page.');
        setIsLoading(false);
        return null;
      }
      
      console.log('Initializing Asteroids game engine');
      
      // Initialize the game engine
      const engine = new AsteroidsGameEngine(canvasRef.current);
      setGameEngine(engine);
      
      // Start the game
      engine.start();
      console.log('Asteroids game engine started');
      setIsLoading(false);
      
      // Record successful initialization in analytics
      recordAnalyticsEvent('game_initialized', {
        game: 'asteroids',
        browser: navigator.userAgent
      });
      
      toast({
        title: "Game Ready",
        description: "Asteroids game loaded successfully!",
        duration: 3000,
      });
      
      return engine;
    } catch (err) {
      console.error("Failed to initialize Asteroids game:", err);
      setError("Failed to initialize the Asteroids game. Please try refreshing the page.");
      setIsLoading(false);
      
      // Record error in analytics
      recordAnalyticsEvent('game_error', {
        game: 'asteroids',
        error: err instanceof Error ? err.message : 'Unknown error',
        browser: navigator.userAgent
      });
      
      return null;
    }
  }, [canvasRef, toast]);
  
  // Initialize the game engine when the component mounts
  useEffect(() => {
    let engine: AsteroidsGameEngine | null = null;
    
    const init = () => {
      if (canvasRef.current && containerRef.current) {
        console.log('Initializing Asteroids game with canvas and container refs');
        updateCanvasSize();
        
        // Add a small delay to ensure canvas is ready
        setTimeout(() => {
          engine = initializeEngine();
        }, 300);
      } else {
        console.error('Canvas or container refs not available');
        setError('Could not initialize game. Please refresh the page.');
        setIsLoading(false);
      }
    };
    
    init();
    
    // Clean up when the component unmounts
    return () => {
      if (engine) {
        console.log('Cleaning up Asteroids game engine');
        engine.stop();
      }
    };
  }, [canvasRef, containerRef, updateCanvasSize, initializeEngine]);
  
  // Set up window resize listener
  useEffect(() => {
    console.log('Setting up window resize listener for Asteroids game');
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [updateCanvasSize]);
  
  const retryInitialization = useCallback(() => {
    console.log('Retrying Asteroids game initialization');
    setIsLoading(true);
    setError(null);
    
    // Record retry attempt in analytics
    recordAnalyticsEvent('game_retry', {
      game: 'asteroids',
      browser: navigator.userAgent
    });
    
    if (canvasRef.current && containerRef.current) {
      // Use the updateCanvasSize function for consistency
      updateCanvasSize();
      
      // Delay initialization to ensure canvas is ready
      setTimeout(() => {
        initializeEngine();
      }, 300);
    } else {
      setError('Could not initialize game. Canvas or container not available.');
      setIsLoading(false);
    }
  }, [canvasRef, containerRef, updateCanvasSize, initializeEngine]);
  
  return {
    gameEngine,
    isLoading,
    error,
    updateCanvasSize,
    retryInitialization
  };
};
